// v10/src/components/VoiceAssistant.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Volume2, Mic, MicOff, VolumeX, Volume, Moon, Sun, Settings } from 'lucide-react';
import { stopSpeech } from '../utils/speechHelper';
import { useTranslation } from 'react-i18next';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

const getAssistantName = () => {
  const lang = localStorage.getItem('condor360_language') || 'es-AR';
  if (lang === 'en') return 'Lauren';
  return 'Valentina';
};
const CONFIRM_KEYWORDS  = ['listo', 'ok', 'confirmar', 'chequeado', 'sí', 'si', 'hecho', 'next', 'siguiente'];

const AudioWave = ({ active, listening, darkMode }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '3px', height: '24px' }}>
    {[0,1,2,3,4,5,6,7,8,9].map((i) => (
      <div key={i} style={{
        width: '3px', borderRadius: '2px',
        backgroundColor: listening
          ? `rgba(34,197,94,${0.5 + (i % 3) * 0.2})`
          : active
          ? darkMode
            ? `rgba(99,210,255,${0.5 + (i % 3) * 0.2})`
            : `rgba(37,99,235,${0.4 + (i % 3) * 0.2})`
          : darkMode ? 'rgba(99,210,255,0.15)' : 'rgba(37,99,235,0.15)',
        height: (active || listening) ? undefined : '3px',
        animation: (active || listening) ? `va-wave-${i % 4} ${0.5 + (i % 4) * 0.1}s ease-in-out infinite alternate` : 'none',
        animationDelay: `${i * 0.06}s`,
        transition: 'height 0.2s ease',
      }} />
    ))}
    <style>{`
      @keyframes va-wave-0 { from { height: 4px } to { height: 18px } }
      @keyframes va-wave-1 { from { height: 7px } to { height: 22px } }
      @keyframes va-wave-2 { from { height: 5px } to { height: 16px } }
      @keyframes va-wave-3 { from { height: 9px } to { height: 20px } }
      @keyframes va-pulse-ring {
        0% { transform: scale(1); opacity: 0.5 }
        100% { transform: scale(1.6); opacity: 0 }
      }
      @keyframes va-blink {
        0%, 100% { opacity: 1 }
        50% { opacity: 0.3 }
      }
    `}</style>
  </div>
);

const MicButton = ({ active, listening, onPressStart, onPressEnd, disabled, darkMode }) => (
  <div style={{ position: 'relative', width: '52px', height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {listening && (
      <>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1.5px solid rgba(34,197,94,0.6)', animation: 'va-pulse-ring 1.2s ease-out infinite', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', inset: '-8px', borderRadius: '50%', border: '1px solid rgba(34,197,94,0.25)', animation: 'va-pulse-ring 1.2s ease-out infinite 0.4s', pointerEvents: 'none' }} />
      </>
    )}
    {active && !listening && !disabled && (
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: darkMode ? '1.5px solid rgba(99,210,255,0.5)' : '1.5px solid rgba(37,99,235,0.4)', animation: 'va-pulse-ring 1.6s ease-out infinite', pointerEvents: 'none' }} />
    )}
    <motion.button
      onMouseDown={!disabled ? onPressStart : undefined}
      onMouseUp={!disabled ? onPressEnd : undefined}
      onMouseLeave={!disabled ? onPressEnd : undefined}
      onTouchStart={!disabled ? (e) => { e.preventDefault(); onPressStart && onPressStart(); } : undefined}
      onTouchEnd={!disabled ? (e) => { e.preventDefault(); onPressEnd && onPressEnd(); } : undefined}
      disabled={disabled}
      whileHover={!disabled ? { scale: 1.08 } : {}}
      whileTap={!disabled ? { scale: 0.93 } : {}}
      style={{
        width: '48px', height: '48px', borderRadius: '50%',
        background: listening
          ? 'linear-gradient(135deg, rgba(34,197,94,0.25), rgba(16,185,129,0.15))'
          : active && !disabled
          ? darkMode
            ? 'linear-gradient(135deg, rgba(99,210,255,0.22), rgba(56,155,255,0.14))'
            : 'linear-gradient(135deg, rgba(37,99,235,0.15), rgba(59,130,246,0.1))'
          : darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(37,99,235,0.07)',
        border: `1.5px solid ${
          listening ? 'rgba(34,197,94,0.6)'
          : active && !disabled
          ? darkMode ? 'rgba(99,210,255,0.55)' : 'rgba(37,99,235,0.45)'
          : darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(37,99,235,0.2)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.25s ease',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {listening
        ? <MicOff style={{ width: '20px', height: '20px', color: 'rgba(34,197,94,0.9)' }} />
        : <Mic style={{ width: '20px', height: '20px', color: active && !disabled
            ? darkMode ? 'rgba(99,210,255,0.9)' : 'rgba(37,99,235,0.9)'
            : darkMode ? 'rgba(255,255,255,0.45)' : 'rgba(37,99,235,0.4)' }} />
      }
    </motion.button>
  </div>
);

const ActionBtn = ({ onClick, icon, label, variant = 'default', title, darkMode }) => {
  const styles = {
    dark: {
      default: { bg: 'rgba(255,255,255,0.07)', hover: 'rgba(255,255,255,0.13)', border: 'rgba(255,255,255,0.1)',   color: 'rgba(255,255,255,0.75)' },
      danger:  { bg: 'rgba(255,80,80,0.08)',   hover: 'rgba(255,80,80,0.18)',   border: 'rgba(255,100,100,0.22)', color: 'rgba(255,150,150,0.9)'  },
      accent:  { bg: 'rgba(99,210,255,0.08)',  hover: 'rgba(99,210,255,0.16)',  border: 'rgba(99,210,255,0.22)',  color: 'rgba(99,210,255,0.9)'   },
    },
    light: {
      default: { bg: 'rgba(255,255,255,0.8)',  hover: 'rgba(255,255,255,1)',    border: 'rgba(59,130,246,0.15)', color: 'rgba(15,30,70,0.75)'   },
      danger:  { bg: 'rgba(255,80,80,0.08)',   hover: 'rgba(255,80,80,0.15)',   border: 'rgba(220,50,50,0.25)', color: 'rgba(180,30,30,0.9)'   },
      accent:  { bg: 'rgba(37,99,235,0.08)',   hover: 'rgba(37,99,235,0.15)',   border: 'rgba(37,99,235,0.25)', color: 'rgba(37,99,235,0.9)'   },
    },
  };
  const s = (darkMode ? styles.dark : styles.light)[variant];
  return (
    <motion.button onClick={onClick} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} title={title}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '9px 8px', borderRadius: '10px', background: s.bg, border: `1px solid ${s.border}`, color: s.color, fontSize: '12.5px', fontWeight: '600', letterSpacing: '0.2px', cursor: 'pointer', backdropFilter: 'blur(6px)', whiteSpace: 'nowrap', fontFamily: 'inherit', transition: 'background 0.2s ease' }}
      onMouseEnter={e => e.currentTarget.style.background = s.hover}
      onMouseLeave={e => e.currentTarget.style.background = s.bg}>
      {icon}
      {label && <span>{label}</span>}
    </motion.button>
  );
};

const VoiceAssistant = ({
  currentItem, isChecking, onVoiceConfirm, voiceEnabled, onToggleVoice,
  justConfirmed, darkMode, onToggleDarkMode, onShowVoiceSelector,
  isSpeaking
}) => {
  const { t } = useTranslation();

  const [listening,        setListening]        = useState(false);
  const [isPressing,       setIsPressing]        = useState(false); // push-to-talk
  const [transcript,       setTranscript]       = useState('');
  const [speechSupported,  setSpeechSupported]  = useState(!!SpeechRecognition);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const recognitionRef = useRef(null);
  const listeningRef   = useRef(false);
  const sessionIdRef    = useRef(0);
  const watchdogRef     = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const isCheckingRef    = useRef(isChecking);
  const voiceEnabledRef  = useRef(voiceEnabled);
  const justConfirmedRef = useRef(justConfirmed);
  const isSpeakingRef    = useRef(isSpeaking);

  useEffect(() => { isCheckingRef.current    = isChecking;    }, [isChecking]);
  useEffect(() => { voiceEnabledRef.current  = voiceEnabled;  }, [voiceEnabled]);
  useEffect(() => { justConfirmedRef.current = justConfirmed; }, [justConfirmed]);
  useEffect(() => { isSpeakingRef.current    = isSpeaking;    }, [isSpeaking]);

  // ✅ FIX: isSpeaking ya NO deshabilita el botón visualmente.
  // El mic se ve activo mientras Valentina habla, y arranca a escuchar
  // automáticamente apenas termina (controlado por el useEffect de abajo).
  const micDisabled = !isChecking || !voiceEnabled || justConfirmed
                   || !speechSupported || permissionDenied;

  const initRecognition = useCallback(() => {
    if (!SpeechRecognition) return null;

    const rec = new SpeechRecognition();
    const voices = window.speechSynthesis?.getVoices?.() || [];
    const hasAR  = voices.some(v => v.lang === 'es-AR' || v.lang === 'es_AR');
    rec.lang            = hasAR ? 'es-AR' : 'es-ES';
    rec.continuous      = false;
    rec.interimResults  = false;
    rec.maxAlternatives = 3;

    const mySessionId = sessionIdRef.current;

    rec.onstart = () => {
      if (mySessionId !== sessionIdRef.current) return;
      listeningRef.current = true;
      lastActivityRef.current = Date.now();
      setListening(true);
      setTranscript('');
    };

    rec.onresult = (event) => {
      if (mySessionId !== sessionIdRef.current) return;
      lastActivityRef.current = Date.now();
      const results = Array.from(event.results)
        .flatMap(r => Array.from(r))
        .map(alt => alt.transcript.toLowerCase().trim());

      console.log('[SR] onresult:', results);
      setTranscript(results[0] || '');

      const matched = results.some(text =>
        CONFIRM_KEYWORDS.some(kw => text.includes(kw))
      );

      if (matched) {
        stopSpeech();
        onVoiceConfirm();
        stopListening();
      } else {
        setTimeout(() => {
          if (mySessionId === sessionIdRef.current && listeningRef.current) startListening();
        }, 300);
      }
    };

    rec.onerror = (event) => {
      if (mySessionId !== sessionIdRef.current) return;
      if (event.error === 'not-allowed' || event.error === 'permission-denied') {
        setPermissionDenied(true);
        setListening(false);
        listeningRef.current = false;
        return;
      }
      if (event.error === 'no-speech' || event.error === 'aborted') {
        if (listeningRef.current && isCheckingRef.current && voiceEnabledRef.current
            && !justConfirmedRef.current && !isSpeakingRef.current) {
          setTimeout(() => { if (mySessionId === sessionIdRef.current) startListening(); }, 400);
        }
        return;
      }
      setListening(false);
      listeningRef.current = false;
    };

    rec.onend = () => {
      if (mySessionId !== sessionIdRef.current) return;
      if (listeningRef.current && isCheckingRef.current && voiceEnabledRef.current
          && !justConfirmedRef.current && !isSpeakingRef.current) {
        setTimeout(() => { if (mySessionId === sessionIdRef.current) startListening(); }, 200);
      } else {
        setListening(false);
        listeningRef.current = false;
      }
    };

    return rec;
  // eslint-disable-next-line
  }, [onVoiceConfirm]);

  const startListening = useCallback(() => {
    if (!speechSupported || permissionDenied) return;
    try {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (_) {}
      }
      recognitionRef.current = initRecognition();
      if (recognitionRef.current) {
        listeningRef.current = true;
        lastActivityRef.current = Date.now();
        recognitionRef.current.start();
      }
    } catch (err) {
      console.warn('[VoiceAssistant] start error:', err.message);
    }
  }, [speechSupported, permissionDenied, initRecognition]);

  const stopListening = useCallback(() => {
    sessionIdRef.current++;
    listeningRef.current = false;
    setListening(false);
    setTranscript('');
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (_) {}
      recognitionRef.current = null;
    }
  }, []);

  // ✅ PTT: no arrancar automáticamente — el piloto controla cuándo hablar
  // Solo detener cuando termina el chequeo o se deshabilita la voz
  useEffect(() => {
    if (!isChecking || !voiceEnabled || justConfirmed) {
      stopListening();
      setIsPressing(false);
    }
  // eslint-disable-next-line
  }, [isChecking, voiceEnabled, justConfirmed]);

  useEffect(() => {
    return () => stopListening();
  }, [stopListening]);

  // ✅ Watchdog: reinicia el mic si Chrome lo mató silenciosamente
  useEffect(() => {
    const tick = () => {
      if (!listeningRef.current || !isCheckingRef.current
          || !voiceEnabledRef.current || justConfirmedRef.current
          || isSpeakingRef.current) return;
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed > 6000) {
        lastActivityRef.current = Date.now();
        startListening();
      }
    };
    watchdogRef.current = setInterval(tick, 3000);
    return () => { if (watchdogRef.current) clearInterval(watchdogRef.current); };
  }, [startListening]);

  // ✅ FIX 2: reiniciar mic tras visibilitychange o scroll en Chrome Android
  // Chrome Android suspende el mic cuando el usuario scrollea o minimiza
  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible'
          && listeningRef.current && isCheckingRef.current
          && voiceEnabledRef.current && !justConfirmedRef.current) {
        setTimeout(() => startListening(), 600);
      }
    };

    let scrollTimer = null;
    const handleScroll = () => {
      // Al terminar el scroll, reiniciar si estaba escuchando
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        if (listeningRef.current && isCheckingRef.current
            && voiceEnabledRef.current && !justConfirmedRef.current
            && !isSpeakingRef.current) {
          startListening();
        }
      }, 500);
    };

    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer);
    };
  }, [startListening, stopListening]);

  // ✅ Push-to-talk: el piloto mantiene presionado para hablar
  const handleMicPressStart = () => {
    if (micDisabled) return;
    setIsPressing(true);
    stopSpeech();
    startListening();
  };

  const handleMicPressEnd = () => {
    if (!isPressing) return;
    setIsPressing(false);
    // Dar 800ms para que el recognition procese lo que se dijo antes de detener
    setTimeout(() => {
      if (!listeningRef.current) return;
      stopListening();
    }, 800);
  };

  const statusText = permissionDenied
    ? t('mic_blocked',{ns:'common'})
    : !speechSupported
    ? 'Voz no disponible'
    : isSpeaking      ? `${getAssistantName()} hablando...`
    : !isChecking     ? 'En espera'
    : listening       ? `Escuchando... "${transcript || 'listo'}"`
    : voiceEnabled    ? t('hold_to_speak',{ns:'common'})
    : 'Voz silenciada';

  const statusColor = permissionDenied || !speechSupported
    ? 'rgba(248,113,113,0.85)'
    : !voiceEnabled   ? 'rgba(248,113,113,0.85)'
    : isSpeaking      ? 'rgba(251,191,36,0.9)'
    : listening       ? 'rgba(34,197,94,0.9)'
    : isChecking      ? 'rgba(100,116,139,0.7)'
    : 'rgba(100,116,139,0.6)';

  const dotColor = permissionDenied ? '#f87171'
    : !voiceEnabled ? '#f87171'
    : isSpeaking    ? '#fbbf24'
    : listening     ? '#4ade80'
    : isChecking    ? 'rgba(100,116,139,0.5)'
    : 'rgba(100,116,139,0.3)';

  return (
    <>
    <motion.div
      initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}
      style={{
        borderRadius: '20px', marginBottom: '24px',
        background: darkMode
          ? 'linear-gradient(145deg, #0f1b35 0%, #0e1628 40%, #161030 100%)'
          : 'white',
        border: darkMode ? '1px solid rgba(99,210,255,0.12)' : '1px solid rgba(226,232,240,1)',
        boxShadow: darkMode
          ? '0 8px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)'
          : '0 1px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden', position: 'relative',
      }}
    >
      {darkMode && (
        <>
          <div style={{ position:'absolute', inset:0, opacity:0.03, backgroundImage:'linear-gradient(rgba(99,210,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(99,210,255,1) 1px, transparent 1px)', backgroundSize:'32px 32px', pointerEvents:'none' }} />
          <div style={{ position:'absolute', top:'-40px', left:'50%', transform:'translateX(-50%)', width:'220px', height:'80px', background:'radial-gradient(ellipse, rgba(99,210,255,0.1), transparent 70%)', pointerEvents:'none' }} />
        </>
      )}

      <div style={{ position: 'relative', padding: '18px 20px' }}>

        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
            <div data-tour="voice-btn">
              <MicButton
                active={isChecking && voiceEnabled}
                listening={listening}
                onPressStart={handleMicPressStart}
                onPressEnd={handleMicPressEnd}
                disabled={micDisabled}
                darkMode={darkMode}
              />
            </div>
            <div>
              <p style={{ margin:0, fontWeight:'700', fontSize:'14px', color: darkMode?'rgba(255,255,255,0.92)':'rgba(15,30,70,0.9)', letterSpacing:'0.3px' }}>
                {isChecking ? 'Asistente Activo' : 'Listo para Chequeo'}
              </p>
              <div style={{ display:'flex', alignItems:'center', gap:'6px', marginTop:'4px' }}>
                <span style={{ width:'6px', height:'6px', borderRadius:'50%', background: dotColor, boxShadow:`0 0 6px ${dotColor}`, display:'inline-block', animation: (listening || isSpeaking) ? 'va-blink 1s ease-in-out infinite' : 'none' }} />
                <span style={{ fontSize:'11.5px', color: statusColor, fontWeight:'500' }}>{statusText}</span>
              </div>
              {isChecking && voiceEnabled && !listening && !isSpeaking && !permissionDenied && speechSupported && (
                <div style={{ marginTop:'3px', display:'flex', alignItems:'center', gap:'4px' }}>
                  <span style={{ fontSize:'9px', color: darkMode?'rgba(255,255,255,0.25)':'rgba(60,80,120,0.4)', fontWeight:600, letterSpacing:'0.05em' }}>
                    🎤 Mantené presionado · toca el ítem para confirmar
                  </span>
                </div>
              )}
            </div>
          </div>
          <AudioWave active={isChecking && voiceEnabled && !listening} listening={listening} darkMode={darkMode} />
        </div>

        {permissionDenied && (
          <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }}
            style={{ marginBottom:'12px', padding:'8px 12px', borderRadius:'10px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', fontSize:'12px', color:'#dc2626', fontWeight:500, lineHeight:1.4 }}>
            🎤 Permiso de micrófono denegado. Habilitalo en la configuración del navegador para usar comandos de voz. Podés seguir usando el toque manual.
          </motion.div>
        )}

        {!speechSupported && (
          <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }}
            style={{ marginBottom:'12px', padding:'8px 12px', borderRadius:'10px', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)', fontSize:'12px', color:'#92400e', fontWeight:500, lineHeight:1.4 }}>
            ⚠️ Tu navegador no soporta reconocimiento de voz. Usá Chrome o Safari para activar los comandos de voz.
          </motion.div>
        )}

        <div style={{ height:'1px', background: darkMode?'rgba(99,210,255,0.08)':'rgba(226,232,240,1)', margin:'0 0 14px' }} />

        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'8px' }}>
          <ActionBtn onClick={onShowVoiceSelector} title="Configurar voz del asistente"
            icon={<Settings style={{ width:'14px', height:'14px' }} />} label="Voz" variant="default" darkMode={darkMode} />
          <ActionBtn onClick={onToggleVoice}
            icon={voiceEnabled
              ? <Volume style={{ width:'14px', height:'14px' }} />
              : <VolumeX style={{ width:'14px', height:'14px' }} />}
            label={voiceEnabled ? 'Apagar' : 'Encender'}
            variant={voiceEnabled ? 'danger' : 'accent'} darkMode={darkMode} />
          <ActionBtn onClick={onToggleDarkMode}
            icon={darkMode
              ? <Sun style={{ width:'14px', height:'14px', color:'#fde68a' }} />
              : <Moon style={{ width:'14px', height:'14px' }} />}
            label={darkMode ? 'Claro' : 'Oscuro'} variant="default" darkMode={darkMode} />
        </div>

        <div style={{ marginTop:'14px', padding:'8px 14px', borderRadius:'10px', background: darkMode?'rgba(99,210,255,0.04)':'rgba(248,250,252,1)', border: darkMode?'1px solid rgba(99,210,255,0.07)':'1px solid rgba(226,232,240,1)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:'10.5px', color: darkMode?'rgba(255,255,255,0.25)':'rgba(100,116,139,0.7)', fontFamily:'monospace', letterSpacing:'0.5px' }}>
            VOICE MODULE · v2.3
          </span>
          <span style={{ fontSize:'10.5px', color: darkMode?'rgba(99,210,255,0.4)':'rgba(37,99,235,0.5)', fontFamily:'monospace' }}>
            {isSpeaking ? '🔊 SPEAKING' : listening ? '🎤 LISTENING' : isChecking ? 'ATC · ACTIVE' : 'ATC · STANDBY'}
          </span>
        </div>

      </div>
    </motion.div>

    {/* ✅ Botón PTT flotante — siempre visible al scrollear */}
    {isChecking && voiceEnabled && !justConfirmed && speechSupported && !permissionDenied && (
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '16px',
          zIndex: 55,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
          pointerEvents: 'auto',
        }}
      >
        {/* Label flotante */}
        {!listening && !isSpeaking && (
          <motion.div
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            style={{ background: darkMode ? 'rgba(15,25,50,0.92)' : 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(37,99,235,0.15)', borderRadius: '10px', padding: '4px 10px', fontSize: '10px', fontWeight: 700, color: darkMode ? 'rgba(147,197,253,0.85)' : '#2563eb', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
            Mantené presionado
          </motion.div>
        )}
        {listening && (
          <motion.div
            animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 1, repeat: Infinity }}
            style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.35)', borderRadius: '10px', padding: '4px 10px', fontSize: '10px', fontWeight: 700, color: '#22c55e', whiteSpace: 'nowrap' }}>
            Escuchando...
          </motion.div>
        )}
        {isSpeaking && (
          <div style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.35)', borderRadius: '10px', padding: '4px 10px', fontSize: '10px', fontWeight: 700, color: '#d97706', whiteSpace: 'nowrap' }}>
            {getAssistantName()} hablando
          </div>
        )}

        {/* Botón PTT */}
        <motion.button
          onMouseDown={handleMicPressStart}
          onMouseUp={handleMicPressEnd}
          onMouseLeave={handleMicPressEnd}
          onTouchStart={(e) => { e.preventDefault(); handleMicPressStart(); }}
          onTouchEnd={(e) => { e.preventDefault(); handleMicPressEnd(); }}
          whileTap={{ scale: 0.92 }}
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: listening
              ? 'linear-gradient(135deg, rgba(34,197,94,0.9), rgba(16,185,129,0.85))'
              : isSpeaking
              ? 'linear-gradient(135deg, rgba(251,191,36,0.9), rgba(245,158,11,0.85))'
              : darkMode
              ? 'linear-gradient(135deg, rgba(37,99,235,0.9), rgba(109,40,217,0.85))'
              : 'linear-gradient(135deg, #2563eb, #7c3aed)',
            boxShadow: listening
              ? '0 4px 20px rgba(34,197,94,0.5)'
              : isSpeaking
              ? '0 4px 20px rgba(251,191,36,0.4)'
              : '0 4px 20px rgba(37,99,235,0.4)',
            fontSize: '24px',
            transition: 'all 0.2s ease',
          }}
        >
          {listening ? '🎤' : isSpeaking ? '🔊' : '🎙️'}
        </motion.button>
      </div>
    )}
    </>
  );
};

export default VoiceAssistant;
